import { CheckoutForm, OrderSummary } from "@/components";
import {
  createOrders,
  createQuickReorder,
  updateQuickReorder,
  updateUserShippingAddress,
  createUserBilling,
  updateUserBilling,
  createUserShippingAddress,
} from "@/graphql/mutations";
import { checkoutSchema } from "@/schemas/checkoutSchema";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { executeGraphQLMutation, fetchAllWithPagination } from "@/utils/helper/graphql-helper";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { v4 as uuidv4 } from "uuid";
import { generateClient } from "@/utils/helper/aws-generateClient-singleton";

import { listQuickReorders, listUserShippingAddresses } from "@/graphql/queries";
import { addOrderHistory } from "@/store/slices/appSlice";
import { useNavigate } from "react-router-dom";
import { UserBilling } from "@/types/AppTypes";
import { useGetCustomerPaymentMethods } from "@/hooks/stripe/useGetCustomerPaymentMethods";
import { fetchUserBilling } from "@/components/userPaymentMethod/UserPaymentMethod";
import { v4 as uuid } from "uuid";
import { parseJSONField } from "@/utils/helper/parseJSON";
import { Loader2 } from "lucide-react";
import { useCartManagement } from "@/hooks/useCartManagement";
type CheckoutFormValues = z.infer<typeof checkoutSchema>;

const Checkout = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shipping] = useState(0);
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const client = generateClient();
  const navigate = useNavigate();
  const { cartItems, clearCart } = useCartManagement();

  const subtotal = useMemo(
    () =>
      cartItems?.reduce((total, item) => total + ((item?.price || item?.variant?.price) * (item?.quantity ?? 1)), 0) ||
      0,
    [cartItems]
  );
  const tax = useMemo(() => subtotal * 0.00, [subtotal]);
  const total = useMemo(() => subtotal + shipping + tax, [subtotal, tax]);

  const [userExistingStripePaymentMethods, setUserExistingStripePaymentMethods] = useState<any[]>(
    []
  );
  const [userBilling, setUserBilling] = useState<UserBilling | undefined>(undefined);
  const [userShippingAddress, setUserShippingAddress] = useState<any>(null);
  const [saveShippingAddress, setSaveShippingAddress] = useState(false);
  const [shippingAddressName, setShippingAddressName] = useState("");
  const [selectedExistingAddress, setSelectedExistingAddress] = useState<any>(null);
  const { getPaymentMethods } = useGetCustomerPaymentMethods();
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    const handleFetchUserPaymentDetails = async () => {
      try {
        const userBilling = await fetchUserBilling(user?.user_id || "");

        if (userBilling && userBilling.customer_id) {
          setUserBilling(userBilling);
          const methods = await getPaymentMethods(userBilling?.customer_id || "");

          if (methods && methods.data) {
            setUserExistingStripePaymentMethods(methods?.data);
          }
        }
      } catch (err) {
        console.error("Failed to get payment methods:", err);
      }
    };

    if (user && user.user_id) {
      setIsLoading(true);
      Promise.all([
        handleFetchUserPaymentDetails(),
        fetchUserShippingAddress(user?.user_id),
      ]).finally(() => {
        setIsLoading(false);
      });
    }
  }, []);

  const handlePurchase = useCallback(
    async (data: CheckoutFormValues) => {
      setIsSubmitting(true);

      try {
        toast.loading("Processing your order...");

        let billingResult = null;
        if (userBilling) {
          if (!userBilling.payment_id?.includes(data?.stripePaymentMethodId || "")) {
            billingResult = await executeGraphQLMutation({
              query: updateUserBilling,
              variables: {
                input: {
                  id: userBilling.id,
                  payment_id: [...(userBilling.payment_id || []), data?.stripePaymentMethodId],
                  updated_at: new Date().toISOString(),
                },
              },
            });

            if (!billingResult) {
              toast.dismiss();
              throw new Error("Billing update failed");
            }
          }
        } else {
          billingResult = await executeGraphQLMutation({
            query: createUserBilling,
            variables: {
              input: {
                id: uuid(),
                user_id: user?.user_id || "",
                customer_id: data.customerId,
                payment_id: [data?.stripePaymentMethodId],
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              },
            },
          });

          if (!billingResult) {
            toast.dismiss();
            throw new Error("Billing creation failed");
          }
        }

        // Determine shipping address source
        let shippingAddress;
  
        shippingAddress = {
          name: shippingAddressName || `Address ${new Date().toISOString()}`,

          street: data?.street,
          suite: data?.suite,
          city: data?.city,
          state: data?.state,
          postal_code: data?.zipCode,
          country: data?.country,
        };

        let shippingResult = null;

        if (saveShippingAddress && !selectedExistingAddress) {
          const addressesToSave = userShippingAddress?.shipping_address
            ? [...userShippingAddress.shipping_address, JSON.stringify(shippingAddress)]
            : [JSON.stringify(shippingAddress)];

          if (!userShippingAddress) {
            shippingResult = await executeGraphQLMutation({
              query: createUserShippingAddress,
              variables: {
                input: {
                  id: uuid(),
                  user_id: user?.user_id || "",
                  shipping_address: addressesToSave,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                },
              },
            });
          } else {
            //const filterAddress = userShippingAddress.shipping_address.find((address: any) => address.name === .name);

            shippingResult = await executeGraphQLMutation({
              query: updateUserShippingAddress,
              variables: {
                input: {
                  id: userShippingAddress.id,
                  shipping_address: saveShippingAddress
                    ? addressesToSave
                    : userShippingAddress.shipping_address,
                  updated_at: new Date().toISOString(),
                },
              },
            });
          }

          if (!shippingResult && (!userShippingAddress || saveShippingAddress)) {
            toast.dismiss();
            throw new Error("Shipping address operation failed");
          }
        }

        let orders = [];

        if (cartItems && cartItems.length > 0) {
          for (const item of cartItems) {
            const order = await executeGraphQLMutation({
              query: createOrders,
              variables: {
                input: {
                  id: uuidv4(),
                  user_id:  user?.user_id,
                  product_id:  item?.product_id,
                  quantity: item?.quantity,
                  seller_id: item?.seller_id || item?.variant?.seller_id,
                  brand_id: item?.brand_id || item?.variant?.brand_id,
                  category_id: item?.category_id || item?.variant?.category_id,
                  status: "PENDING",
                  shipping_address: JSON.stringify({
                    ...shippingAddress,
                    first_name: data?.firstName,
                    last_name: data?.lastName,
                    email: data?.email,
                    phone: data?.phone,
                  }),
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                  variant: JSON.stringify(item?.variant),
                },
              },
              client,
            });
            orders.push(order);
            dispatch(addOrderHistory(order));
          }
        }

        let quickReorder = [];

        if (orders.length > 0) {
          for (const item of cartItems) {
            const checkOldReorders = await fetchAllWithPagination({
              query: listQuickReorders,
              variables: {
                filter: {
                  user_id: {
                    eq: user?.user_id,
                  },
                  product_id: {
                    eq: item?.product_id,
                  },
                },
              },
            });
            if (checkOldReorders && checkOldReorders.length > 0) {
              const updatedQuickReorder = await executeGraphQLMutation({
                query: updateQuickReorder,
                variables: {
                  input: {
                    id: checkOldReorders[0]?.id,
                    stock: checkOldReorders[0]?.stock + item?.quantity,
                    updated_at: new Date().toISOString(),
                    last_order: new Date().toISOString(),
                  },
                },
              });
              quickReorder.push(updatedQuickReorder);
            } else {
              const newQuickReorder = await executeGraphQLMutation({
                query: createQuickReorder,
                variables: {
                  input: {
                    id: uuidv4(),
                    user_id: user?.user_id,
                    product_id: item?.product_id,
                    stock: item?.quantity,
                    variant: JSON.stringify(item),
                    last_order: new Date().toISOString(),
                    brand_id: item?.brand_id,
                    category_id: item?.category_id,
                    seller_id: item?.seller_id,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                  },
                },
                client,
              });
              quickReorder.push(newQuickReorder);
            }
          }

          // const updateUserData = await executeGraphQLMutation({
          //   query: updateUserInfo,
          //   variables: {
          //     input: {
          //       id: user?.id,
          //       phone: data?.phone,
          //       street: data?.street,
          //       suite: data?.suite,
          //       city: data?.city,
          //       state: data?.state,
          //       postal_code: data?.zipCode,
          //       country: data?.country,
          //     },
          //   },
          //   client,
          // });
          // console.log("User updated:", updateUserData);
        }

        toast.dismiss();
        clearCart();

        toast.success("Order placed successfully!", {
          description: "Your order has been placed and will be processed shortly.",
        });

        navigate("/thank-you");
      } catch (error) {
        toast.dismiss();
        toast.error("There was a problem placing your order. ", {
          description: "Please try again.",
        });
        console.error("Error during purchase:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [user, saveShippingAddress, shippingAddressName, selectedExistingAddress]
  );

  const fetchUserShippingAddress = async (userID: string) => {
    try {
      const userShippingAddresses = await executeGraphQLMutation({
        query: listUserShippingAddresses,
        variables: {
          filter: {
            user_id: { eq: userID },
          },
        },
      });

      if (userShippingAddresses?.items?.[0]) {
        setUserShippingAddress(userShippingAddresses.items[0]);
      }
    } catch (error) {
      console.error("Failed to fetch shipping address:", error);
    }
  };

  const handleShippingAddressNameSave = (name: string) => {
    setShippingAddressName(name);
  };

  const handleExistingAddressSelection = (addressName: string) => {
    if (!addressName || !userShippingAddress?.shipping_address) {
      setSelectedExistingAddress(null);
      return;
    }

    // Find the selected address from existing addresses
    const selectedAddress = userShippingAddress.shipping_address.find((addr: any) => {
      const parsedAddr = parseJSONField(addr);
      return parsedAddr?.name === addressName;
    });

    if (selectedAddress) {
      const parsedAddress = parseJSONField(selectedAddress);
      setSelectedExistingAddress(parsedAddress);
      console.log("Selected existing address:", parsedAddress);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <h1 className="text-3xl font-bold mb-8">Checkout</h1>

        {isLoading ? <div className="flex justify-center items-center min-h-[60vh]">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div> :

        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-1">
            <CheckoutForm
              handlePurchase={handlePurchase}
              isSubmitting={isSubmitting}
              totalAmount={total}
              userExistingStripePaymentMethods={userExistingStripePaymentMethods}
              user={user}
              userBilling={userBilling}
              saveShippingAddress={setSaveShippingAddress}
              existingShippingAddress={userShippingAddress?.shipping_address}
              onShippingAddressNameSave={handleShippingAddressNameSave}
              onExistingAddressSelection={handleExistingAddressSelection}
            />
          </div>
          <div className="md:w-96">
            <OrderSummary
              shipping={shipping}
              cartItems={cartItems}
              subtotal={subtotal}
              tax={tax}
              total={total}
            />
          </div>
        </div>
}
      </div>
    </div>
  );
};

export default Checkout;
